import { Select, RadioGroup, Checkbox } from '@/components';
import { WorkInfoProps, SupportArea, WorkStatus, EducationLevel } from './work-info.types';
import { SUPPORT_AREAS, WORK_STATUS } from './work-info.constants';
import '@/assets/styles/components/work-info.styles.scss';
import { UserType } from '@/constants';
import { useApi } from '@/api/useApi.tsx';
import { useMutationProvider, useQueryProvider } from '@/hooks';
import { CreateWorkInformationRequest } from '@dopingtech/live_expert_applicant_bff_api_ts/dist/api';
import React, { useMemo, useEffect, useState } from 'react';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import LoadingSpinner from '@/components/loading-spinner';
import { useQueryClient } from 'react-query';

export const WorkInfo = ({ formData, onFormDataChange, onNext }: WorkInfoProps) => {
	const { ExpertApplicationControllerApi, LiveServiceTypeControllerApi, EducationLevelControllerApi, BranchControllerApi } = useApi();

	const queryClient = useQueryClient();
	const { userType } = formData;
	const [branchesData, setBranchesData] = useState({});
	const [isLoading, setIsLoading] = useState(false);

	const createWorkInformation = useMutationProvider((body: CreateWorkInformationRequest) => ExpertApplicationControllerApi.createWorkInformation(body), {
		onSuccess: () => {
			queryClient.refetchQueries({
				queryKey: [QUERY_KEYS.getLoggedInExpertApplicantDetailInfo]
			});
			onNext();
		},
		onError: () => {
			setIsLoading(false);
		}
	});

	const { data: liveServiceTypes } = useQueryProvider([QUERY_KEYS.getLiveServiceTypes], () => LiveServiceTypeControllerApi.getLiveServiceTypes(), {
		select: ({ data }) => data?.data ?? [],
		refetchOnWindowFocus: false
	});

	const { data: educationLevels = [] } = useQueryProvider([QUERY_KEYS.getEducationLevels], () => EducationLevelControllerApi.getEducationLevels(), {
		select: ({ data }) => data?.data ?? [],
		refetchOnWindowFocus: false
	});

	const levels = useMemo(() => formData?.levels?.map(l => l.level).join(','), [formData?.levels]);

	useQueryProvider(
		[QUERY_KEYS.getBranchesByEducationLevel, levels],
		async () => {
			const newLevels = formData?.levels?.filter(level => level.level && !branchesData[level.level]);

			if (newLevels?.length > 0) {
				const promises = newLevels.map(level => BranchControllerApi.getBranchesByEducationLevel(parseInt(level.level)));

				const results = await Promise.all(promises);

				const newBranchesData = { ...branchesData };
				results.forEach((result, index) => {
					const levelId = newLevels[index].level;
					newBranchesData[levelId] = result.data?.data ?? [];
				});

				setBranchesData(newBranchesData);
			}

			return branchesData;
		},
		{
			enabled: formData?.levels?.length > 0 && !!levels,
			refetchOnWindowFocus: false
		}
	);

	const findHighSchoolId = useMemo<number | undefined>(() => {
		return educationLevels.find((level: EducationLevel) => level?.name === 'Lise')?.id;
	}, [educationLevels]);

	useEffect(() => {
		if (userType === UserType.UNIVERSITY_STUDENT && findHighSchoolId && (!formData?.levels?.length || !formData?.levels?.[0]?.level)) {
			onFormDataChange({
				...formData,
				levels: [{ level: findHighSchoolId.toString(), subjects: [] }]
			});
		}
	}, [userType, findHighSchoolId]);

	const formattedEducationLevels = useMemo(
		() =>
			educationLevels?.map(level => ({
				value: level.id.toString(),
				label: level.name
			})),
		[educationLevels]
	);

	const serviceTypeNameToId = useMemo(() => {
		if (!liveServiceTypes || !Array.isArray(liveServiceTypes)) {
			return {};
		}

		return liveServiceTypes.reduce((acc, item) => {
			if (item && item.name && item.id) {
				acc[item.name] = item.id;
			}
			return acc;
		}, {});
	}, [liveServiceTypes]);

	const handleSupportAreaChange = (value: string) => {
		const area = value as SupportArea;
		const isEducationalUser = userType === UserType.UNIVERSITY_STUDENT || userType === UserType.INSTRUCTOR;

		let newAreas;
		if (isEducationalUser) {
			newAreas = formData.supportAreas?.includes(area) ? formData.supportAreas.filter(a => a !== area) : [...formData.supportAreas, area];
		} else {
			newAreas = [area];
		}

		const updatedFormData = { ...formData, supportAreas: newAreas };

		if (userType === UserType.UNIVERSITY_STUDENT && newAreas.includes('GUIDANCE') && newAreas.length === 1) {
			updatedFormData.levels = formData.levels.map(levelItem => ({
				level: levelItem?.level,
				subjects: []
			}));
		}

		onFormDataChange(updatedFormData);
	};

	const handleWorkStatusChange = (value: string) => {
		onFormDataChange({ ...formData, workStatus: value as WorkStatus });
	};

	const getAvailableLevels = () => {
		const selectedLevels = formData?.levels?.map(l => l.level);
		return formattedEducationLevels.filter(level => !selectedLevels?.includes(level.value));
	};

	const isPreviousLevelComplete = () => {
		if (!formData?.levels?.length) return true;

		const lastLevel = formData.levels[formData.levels.length - 1];
		return lastLevel.subjects.length > 0;
	};

	const handleLevelAdd = () => {
		const availableLevels = getAvailableLevels();
		if (availableLevels?.length > 0 && isPreviousLevelComplete()) {
			const newLevel = availableLevels?.[0]?.value;
			onFormDataChange({
				...formData,
				levels: [...formData.levels, { level: newLevel, subjects: [] }]
			});
		}
	};

	const handleLevelDelete = (index: number) => {
		if (index === 0) return;

		const updatedLevels = formData?.levels?.filter((_, i) => i !== index);
		onFormDataChange({ ...formData, levels: updatedLevels });
	};

	const handleLevelSelect = (index: number, newLevel: string) => {
		if (newLevel === '') return;

		const updatedLevels = formData?.levels?.map((level, i) => {
			if (i === index) {
				return { level: newLevel, subjects: [] };
			}
			return level;
		});
		onFormDataChange({ ...formData, levels: updatedLevels });
	};

	const handleBranchSelect = (levelId: string, selectedSubjects: string[]) => {
		if (!formData?.levels?.length) {
			onFormDataChange({
				...formData,
				levels: [{ level: levelId, subjects: selectedSubjects }]
			});
			return;
		}

		const updatedLevels = formData?.levels?.map(l => {
			if (l.level === levelId) {
				return { ...l, subjects: selectedSubjects };
			}
			return l;
		});
		onFormDataChange({ ...formData, levels: updatedLevels });
	};

	const handleHighSchoolSubjectChange = (subjectValue: string) => {
		if (!formData?.levels?.length || !formData?.levels?.[0]) {
			onFormDataChange({
				...formData,
				levels: [
					{
						level: findHighSchoolId?.toString() || '',
						subjects: [subjectValue]
					}
				]
			});
			return;
		}

		const subjects = formData?.levels?.[0]?.subjects || [];

		if (subjects?.includes(subjectValue)) {
			const newSubjects = subjects.filter(s => s !== subjectValue);
			const updatedLevels = [...formData.levels];
			updatedLevels[0] = { ...updatedLevels[0], subjects: newSubjects };
			onFormDataChange({ ...formData, levels: updatedLevels });
		} else {
			if (subjects?.length >= 3) return;
			const newSubjects = [...subjects, subjectValue];
			const updatedLevels = [...formData.levels];
			updatedLevels[0] = { ...updatedLevels[0], subjects: newSubjects };
			onFormDataChange({ ...formData, levels: updatedLevels });
		}
	};

	const handleLevelChange = (levelId: string, checked: boolean) => {
		const currentLevels = Array.isArray(formData.levels) ? formData.levels : [];

		const updatedLevels = checked ? [...currentLevels, { level: levelId, subjects: [] }] : currentLevels.filter(l => l?.level !== levelId);

		onFormDataChange({ ...formData, levels: updatedLevels });
	};

	const hasAtLeastOneLevelWithSubjects = () => {
		return formData?.levels?.some(level => level.subjects.length > 0);
	};

	const isFormValid = () => {
		if (!formData.workStatus && userType !== UserType.UNIVERSITY_STUDENT) return false;

		if (userType === UserType.INSTRUCTOR) {
			if (formData.supportAreas?.length === 0) return false;
			if (formData?.levels?.length === 0) return false;
			return hasAtLeastOneLevelWithSubjects();
		}

		if (userType === UserType.COACH) {
			return formData?.levels?.length > 0;
		}

		if (userType === UserType.UNIVERSITY_STUDENT) {
			if (formData?.supportAreas?.length === 0) return false;
			if (formData?.supportAreas?.length === 1 && formData?.supportAreas?.[0] === 'GUIDANCE') {
				return true;
			}
			return formData?.levels?.[0]?.subjects?.length > 0;
		}

		return false;
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (isFormValid()) {
			setIsLoading(true);
			let serviceTypeIds = [];
			if (userType === UserType.COACH) {
				serviceTypeIds = [serviceTypeNameToId['GUIDANCE']];
			} else {
				serviceTypeIds = formData?.supportAreas?.map(area => serviceTypeNameToId[area]);
			}

			const branches = formData?.levels?.flatMap(level => {
				if (!level.subjects?.length) {
					if (userType === UserType.COACH) {
						return [
							{
								educationLevelId: parseInt(level.level)
							}
						];
					}

					if (userType === UserType.UNIVERSITY_STUDENT) {
						return [
							{
								educationLevelId: findHighSchoolId
							}
						];
					}
					return [];
				}

				return level.subjects?.map(subject => ({
					educationLevelId: parseInt(level.level),
					branchId: parseInt(subject)
				}));
			});

			createWorkInformation.mutateAsync({
				liveServiceTypeIds: serviceTypeIds,
				schoolType: formData.workStatus || 'UNEMPLOYMENT',
				branches: branches
			});
		}
	};

	const shouldShowSubjects = () => {
		if (userType !== UserType.UNIVERSITY_STUDENT) return true;
		return !(formData.supportAreas?.length === 1 && formData.supportAreas?.[0] === 'GUIDANCE');
	};

	const shouldShowRestOfForm = () => {
		if (userType === UserType.UNIVERSITY_STUDENT) {
			return formData.supportAreas?.length > 0;
		}
		return true;
	};

	const supportAreaOptions = useMemo(() => (userType === UserType.INSTRUCTOR ? SUPPORT_AREAS.expert : SUPPORT_AREAS.guide), [userType]);

	useEffect(() => {
		if (userType === UserType.INSTRUCTOR && formattedEducationLevels?.length > 0 && (!formData?.levels || formData.levels.length === 0)) {
			onFormDataChange({
				...formData,
				levels: [{ level: null, subjects: [] }]
			});
		}
	}, [userType, formattedEducationLevels, formData?.levels]);

	return (
		<div className="work-info-container">
			<form onSubmit={handleSubmit}>
				<div className="form-header">
					<h1>Çalışma Bilgileri</h1>
					<p>Uygulamalarda görev almak istediğiniz alanları doğru ve eksiksiz doldurun.</p>
				</div>

				{userType !== UserType.COACH && (
					<div className="form-section">
						<h3>Bire bir destek sağlayacağınız alanları seçiniz</h3>
						{userType === UserType.UNIVERSITY_STUDENT || userType === UserType.INSTRUCTOR ? (
							<div className="support-areas-grid">
								{supportAreaOptions?.map(option => (
									<Checkbox
										key={option.value}
										label={option.label}
										checked={formData.supportAreas?.includes(option.value as SupportArea)}
										onChange={() => handleSupportAreaChange(option.value)}
										variant="pill"
									/>
								))}
							</div>
						) : (
							<RadioGroup
								options={supportAreaOptions}
								value={formData.supportAreas?.[0] || ''}
								onChange={handleSupportAreaChange}
								name="supportAreas"
							/>
						)}
					</div>
				)}

				{shouldShowRestOfForm() && (
					<>
						{userType !== UserType.UNIVERSITY_STUDENT && (
							<div className="form-section">
								<h3>Çalışma Durumu</h3>
								<RadioGroup
									options={WORK_STATUS}
									value={formData.workStatus}
									onChange={handleWorkStatusChange}
									name="workStatus"
								/>
							</div>
						)}

						{userType === UserType.COACH && (
							<div className="form-section">
								<h3>Destek vermek istediğiniz kademeleri seçin</h3>
								<div className="levels-container">
									{formattedEducationLevels?.map(level => (
										<Checkbox
											key={level.value}
											label={level.label}
											checked={formData?.levels?.some(l => l.level === level.value)}
											onChange={e => handleLevelChange(level.value, e.target.checked)}
											variant="pill"
										/>
									))}
								</div>
							</div>
						)}

						{userType === UserType.INSTRUCTOR && (
							<div className="form-section">
								<h3>Kademe ve Ders Seçimi</h3>
								{formData?.levels?.map((levelData, index) => (
									<div
										key={index}
										className="level-container">
										<div className="level-select-wrapper">
											<Select
												value={levelData.level}
												options={formattedEducationLevels.filter(level => !formData?.levels?.some(l => l.level === level.value && l.level !== levelData.level))}
												onChange={e => handleLevelSelect(index, e.target.value)}
												placeholder="Kademe Seçin"
											/>

											{index > 0 && (
												<button
													type="button"
													className="delete-level-btn"
													onClick={() => handleLevelDelete(index)}
													aria-label="Kademeyi sil">
													<i className="icons-close-circle" />
												</button>
											)}
										</div>

										{levelData.level && (
											<Select
												variant="multiple"
												title="Destek vermek istediğiniz branşları seçin"
												placeholder="Ders seçin"
												options={(branchesData?.[levelData.level] || [])?.map(branch => ({
													value: branch.branchId.toString(),
													label: branch.lessonName
												}))}
												maxSelection={3}
												selectedValues={levelData.subjects}
												onMultipleChange={selected => handleBranchSelect(levelData.level, selected)}
												disabled={!levelData.level}
											/>
										)}
									</div>
								))}

								{getAvailableLevels()?.length > 0 && (
									<button
										type="button"
										className={`add-level ${!isPreviousLevelComplete() ? 'disabled' : ''}`}
										onClick={handleLevelAdd}
										disabled={!isPreviousLevelComplete()}>
										+ Farklı bir kademe ekle
									</button>
								)}
							</div>
						)}

						{userType === UserType.UNIVERSITY_STUDENT && shouldShowSubjects() && (
							<div className="student-form-section">
								<div className="info-wrapper">
									<div className="info-text">
										<span>i</span>
										<p>
											Derece yapmış üniversite öğrencileri yalnızca <strong>lise</strong> kademesinde destek verebilir.
										</p>
									</div>
								</div>

								<div className="subjects-section">
									<h3>Lise derslerine ait destek vermek istediğiniz branşları seçin</h3>
									<div className="info-text">
										<span>i</span>
										<p>
											En fazla <strong>3 ders</strong> seçimi yapılabilir.
										</p>
									</div>

									<div className="subjects-grid">
										{(branchesData[formData?.levels?.[0]?.level] || [])?.map(branch => (
											<Checkbox
												key={branch.branchId}
												checked={formData?.levels?.[0]?.subjects?.includes(branch.branchId.toString())}
												onChange={() => handleHighSchoolSubjectChange(branch.branchId.toString())}
												label={branch.lessonName}
												disabled={formData?.levels?.[0]?.subjects.length >= 3 && !formData?.levels?.[0]?.subjects?.includes(branch.branchId.toString())}
											/>
										))}
									</div>
								</div>
							</div>
						)}
					</>
				)}

				<button
					type="submit"
					className={`submit-button ${!isFormValid() ? 'disabled' : ''}`}
					disabled={!isFormValid()}>
					Başvuruyu Tamamla
				</button>
			</form>
			{isLoading && <LoadingSpinner loadingText={'Yükleniyor'} />}
		</div>
	);
};
